import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function ReportsFinancial() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchReport();
  }, [year]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/reports/financial', {
        params: { year },
      });
      setData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch financial report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/reports/financial/export/csv', {
        params: { year },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial-report-${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export report');
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Financial report" />
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: tokens.primary }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Financial report" />
        <Alert severity="error" sx={{ borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const chartData = data?.income?.map((income, index) => ({
    month: income.month,
    income: income.amount,
    expenditure: data.expenditure[index]?.amount || 0,
  })) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount || 0);
  };

  return (
    <Box>
      <PageHeader
        crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>}
        title="Financial report"
        actions={
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <TextField
              type="number"
              label="Year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              size="small"
              sx={{ width: 120 }}
            />
            <Button
              variant="outlined"
              onClick={handleExport}
              sx={{ borderColor: tokens.border, color: tokens.ink }}
            >
              Export CSV
            </Button>
          </Box>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15, mb: 2 }}>
              Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total income
              </Typography>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 24, fontWeight: 700, color: tokens.primary, fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(data?.summary?.total_income || 0)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total expenditure
              </Typography>
              <Typography sx={{ fontFamily: tokens.fontDisplay, fontSize: 24, fontWeight: 700, color: tokens.danger, fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(data?.summary?.total_expenditure || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: tokens.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Net balance
              </Typography>
              <Typography
                sx={{
                  fontFamily: tokens.fontDisplay,
                  fontSize: 24,
                  fontWeight: 700,
                  color: data?.summary?.net_balance >= 0 ? tokens.primary : tokens.danger,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(data?.summary?.net_balance || 0)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2.5 }}>
            <Typography sx={{ fontFamily: tokens.fontDisplay, fontWeight: 600, fontSize: 15, mb: 2 }}>
              Income vs expenditure ({year})
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} />
                <XAxis dataKey="month" stroke={tokens.muted} />
                <YAxis stroke={tokens.muted} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke={tokens.primary} name="Income" strokeWidth={2} />
                <Line type="monotone" dataKey="expenditure" stroke={tokens.danger} name="Expenditure" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
